import React, { useState, useEffect } from 'react';
import { Palette, Upload, Save, RefreshCw, Eye, CheckCircle, AlertTriangle, X } from 'lucide-react';

interface BrandingSettings {
  systemName: string;
  systemFullName: string;
  systemIcon: string;
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string;
  faviconUrl: string;
  loginPageTitle: string;
  loginPageSubtitle: string;
  loginPageDescription: string;
  loginFeature1: string;
  loginFeature2: string;
  loginFeature3: string;
  loginFeature4: string;
  footerText: string;
  showLoginLeftPanel: boolean;
}

const BrandingSettings: React.FC = () => {
  const [settings, setSettings] = useState<BrandingSettings>({
    systemName: 'PIS',
    systemFullName: 'Pathology Information System',
    systemIcon: '🏥',
    primaryColor: '#3B82F6',
    secondaryColor: '#1E40AF',
    logoUrl: '',
    faviconUrl: '/vite.svg',
    loginPageTitle: 'Welcome to PIS',
    loginPageSubtitle: 'Advanced digital pathology platform',
    loginPageDescription: 'Advanced digital pathology platform for medical imaging, diagnosis management, and laboratory workflow optimization.',
    loginFeature1: '🔬 Digital Pathology Management',
    loginFeature2: '📊 Advanced Analytics & Reports',
    loginFeature3: '🔧 Device Management & Monitoring',
    loginFeature4: '🔒 Secure & HIPAA Compliant',
    footerText: '© 2025 PIS. All rights reserved.',
    showLoginLeftPanel: true
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [uploadError, setUploadError] = useState<string>('');

  const iconOptions = [
    { emoji: '🏥', label: 'Hospital' },
    { emoji: '🔬', label: 'Microscope' },
    { emoji: '🧬', label: 'DNA' },
    { emoji: '💉', label: 'Syringe' },
    { emoji: '🧪', label: 'Test Tube' },
    { emoji: '🩺', label: 'Stethoscope' },
    { emoji: '📊', label: 'Analytics' },
    { emoji: '🔍', label: 'Magnifier' },
    { emoji: '⚕️', label: 'Medical' },
    { emoji: '🏨', label: 'Hospital Building' }
  ];

  const colorPresets = [
    { name: 'Blue', primary: '#3B82F6', secondary: '#1E40AF' },
    { name: 'Green', primary: '#10B981', secondary: '#047857' },
    { name: 'Purple', primary: '#8B5CF6', secondary: '#6D28D9' },
    { name: 'Red', primary: '#EF4444', secondary: '#B91C1C' },
    { name: 'Orange', primary: '#F59E0B', secondary: '#D97706' },
    { name: 'Teal', primary: '#14B8A6', secondary: '#0F766E' }
  ];

  const handleChange = (field: keyof BrandingSettings, value: string | boolean) => {
    setSettings({ ...settings, [field]: value });
    setHasChanges(true);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      // Get auth token
      const token = localStorage.getItem('authToken');

      // Save to backend API
      const response = await fetch('/api/v1/settings/branding/update_active/', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        throw new Error(`Failed to save: ${response.statusText}`);
      }

      const updatedSettings = await response.json();

      // Cache in localStorage for offline/faster loading
      localStorage.setItem('brandingSettings', JSON.stringify(updatedSettings));

      // Update page title immediately
      document.title = `${settings.systemName} - ${settings.systemFullName}`;

      // Update favicon
      const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
      if (favicon) {
        favicon.href = settings.faviconUrl;
      }

      // Dispatch custom event to update header immediately (without refresh)
      window.dispatchEvent(new Event('brandingUpdated'));

      setSaveMessage({
        type: 'success',
        message: 'Branding settings saved successfully! Changes applied immediately.'
      });
      setHasChanges(false);

      setTimeout(() => setSaveMessage(null), 5000);
    } catch (error) {
      console.error('Failed to save branding settings:', error);
      setSaveMessage({
        type: 'error',
        message: `Failed to save settings: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      setTimeout(() => setSaveMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    if (confirm('Are you sure you want to reset to default branding?')) {
      setSettings({
        systemName: 'PIS',
        systemFullName: 'Pathology Information System',
        systemIcon: '🏥',
        primaryColor: '#3B82F6',
        secondaryColor: '#1E40AF',
        logoUrl: '',
        faviconUrl: '/vite.svg',
        loginPageTitle: 'Welcome to PIS',
        loginPageSubtitle: 'Advanced digital pathology platform',
        loginPageDescription: 'Advanced digital pathology platform for medical imaging, diagnosis management, and laboratory workflow optimization.',
        loginFeature1: '🔬 Digital Pathology Management',
        loginFeature2: '📊 Advanced Analytics & Reports',
        loginFeature3: '🔧 Device Management & Monitoring',
        loginFeature4: '🔒 Secure & HIPAA Compliant',
        footerText: '© 2025 PIS. All rights reserved.',
        showLoginLeftPanel: true
      });
      setHasChanges(true);
    }
  };

  const handleApplyColorPreset = (preset: { name: string; primary: string; secondary: string }) => {
    setSettings({
      ...settings,
      primaryColor: preset.primary,
      secondaryColor: preset.secondary
    });
    setHasChanges(true);
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Please upload a valid image file (SVG, PNG, or JPG)');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setUploadError('File size must be less than 5MB');
      return;
    }

    setUploadError('');

    // Convert to base64 for storage
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setLogoPreview(base64String);
      setSettings({ ...settings, logoUrl: base64String });
      setHasChanges(true);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoPreview('');
    setSettings({ ...settings, logoUrl: '' });
    setHasChanges(true);
    setUploadError('');
  };

  // Load settings from API on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Try to fetch from API first
        const response = await fetch('/api/v1/settings/branding/active/');

        if (response.ok) {
          const data = await response.json();
          setSettings(data);
          if (data.logoUrl) {
            setLogoPreview(data.logoUrl);
          }
          // Cache in localStorage
          localStorage.setItem('brandingSettings', JSON.stringify(data));
        } else {
          // Fallback to localStorage if API fails
          const savedSettings = localStorage.getItem('brandingSettings');
          if (savedSettings) {
            const parsed = JSON.parse(savedSettings);
            setSettings(parsed);
            if (parsed.logoUrl) {
              setLogoPreview(parsed.logoUrl);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load branding settings from API:', error);
        // Fallback to localStorage
        const savedSettings = localStorage.getItem('brandingSettings');
        if (savedSettings) {
          try {
            const parsed = JSON.parse(savedSettings);
            setSettings(parsed);
            if (parsed.logoUrl) {
              setLogoPreview(parsed.logoUrl);
            }
          } catch (parseError) {
            console.error('Failed to parse localStorage settings:', parseError);
          }
        }
      }
    };

    loadSettings();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Palette className="w-7 h-7 mr-2 text-blue-600" />
            Branding Settings
          </h2>
          <p className="text-gray-600 mt-1">Customize system name, logo, and colors</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center"
          >
            <Eye className="w-4 h-4 mr-2" />
            {previewMode ? 'Hide Preview' : 'Show Preview'}
          </button>
          <button
            onClick={handleResetToDefault}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset to Default
          </button>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`p-4 rounded-lg border ${
          saveMessage.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center">
            {saveMessage.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <AlertTriangle className="w-5 h-5 mr-2" />
            )}
            {saveMessage.message}
          </div>
        </div>
      )}

      {/* Preview Mode */}
      {previewMode && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border-2 border-blue-200">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Preview</h3>
          <div className="bg-white rounded-lg shadow-lg p-6">
            {/* Header Preview */}
            <div className="flex items-center space-x-3 mb-6 pb-4 border-b">
              {logoPreview || settings.logoUrl ? (
                <div className="w-12 h-12 flex items-center justify-center">
                  <img
                    src={logoPreview || settings.logoUrl}
                    alt="Logo"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <span className="text-3xl">{settings.systemIcon}</span>
              )}
              <div>
                <h1 className="text-xl font-bold" style={{ color: settings.primaryColor }}>
                  {settings.systemName} - {settings.systemFullName}
                </h1>
                <p className="text-sm text-gray-600">{settings.loginPageSubtitle}</p>
              </div>
            </div>

            {/* Button Preview */}
            <div className="space-y-3">
              <button
                className="px-4 py-2 rounded-lg text-white font-medium"
                style={{ backgroundColor: settings.primaryColor }}
              >
                Primary Button
              </button>
              <button
                className="px-4 py-2 rounded-lg text-white font-medium ml-3"
                style={{ backgroundColor: settings.secondaryColor }}
              >
                Secondary Button
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* System Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              System Name (Short)
            </label>
            <input
              type="text"
              value={settings.systemName}
              onChange={(e) => handleChange('systemName', e.target.value)}
              placeholder="e.g., PIS, APACS, PathLab"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Displayed in header and browser tab</p>
          </div>

          {/* System Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              System Full Name
            </label>
            <input
              type="text"
              value={settings.systemFullName}
              onChange={(e) => handleChange('systemFullName', e.target.value)}
              placeholder="e.g., Pathology Information System"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Full description of the system</p>
          </div>

          {/* System Icon */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              System Icon / Emoji
            </label>
            <div className="grid grid-cols-5 md:grid-cols-10 gap-2 mb-3">
              {iconOptions.map((icon) => (
                <button
                  key={icon.emoji}
                  onClick={() => handleChange('systemIcon', icon.emoji)}
                  className={`p-3 text-3xl rounded-lg border-2 transition-all ${
                    settings.systemIcon === icon.emoji
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  title={icon.label}
                >
                  {icon.emoji}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={settings.systemIcon}
              onChange={(e) => handleChange('systemIcon', e.target.value)}
              placeholder="Or paste custom emoji/icon"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Color Presets */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Color Theme Presets
            </label>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
              {colorPresets.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => handleApplyColorPreset(preset)}
                  className="flex flex-col items-center p-3 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-all"
                >
                  <div className="flex space-x-1 mb-2">
                    <div
                      className="w-8 h-8 rounded"
                      style={{ backgroundColor: preset.primary }}
                    />
                    <div
                      className="w-8 h-8 rounded"
                      style={{ backgroundColor: preset.secondary }}
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-700">{preset.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Primary Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Color
            </label>
            <div className="flex space-x-2">
              <input
                type="color"
                value={settings.primaryColor}
                onChange={(e) => handleChange('primaryColor', e.target.value)}
                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={settings.primaryColor}
                onChange={(e) => handleChange('primaryColor', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Main theme color (buttons, links)</p>
          </div>

          {/* Secondary Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Secondary Color
            </label>
            <div className="flex space-x-2">
              <input
                type="color"
                value={settings.secondaryColor}
                onChange={(e) => handleChange('secondaryColor', e.target.value)}
                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={settings.secondaryColor}
                onChange={(e) => handleChange('secondaryColor', e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Accent color (hover states)</p>
          </div>

          {/* Login Page Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Login Page Title
            </label>
            <input
              type="text"
              value={settings.loginPageTitle}
              onChange={(e) => handleChange('loginPageTitle', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Login Page Subtitle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Login Page Subtitle
            </label>
            <input
              type="text"
              value={settings.loginPageSubtitle}
              onChange={(e) => handleChange('loginPageSubtitle', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Footer Text */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Footer Text
            </label>
            <input
              type="text"
              value={settings.footerText}
              onChange={(e) => handleChange('footerText', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Login Page Customization */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span className="text-2xl mr-2">🔐</span>
          Login Page Customization
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Show Left Panel */}
          <div className="md:col-span-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.showLoginLeftPanel}
                onChange={(e) => handleChange('showLoginLeftPanel', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Show Left Branding Panel</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">Display logo, description, and features on the left side of login page</p>
          </div>

          {/* Login Description */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Login Page Description
            </label>
            <textarea
              value={settings.loginPageDescription}
              onChange={(e) => handleChange('loginPageDescription', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Brief description shown on login page left panel"
            />
          </div>

          {/* Feature 1 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Feature 1
            </label>
            <input
              type="text"
              value={settings.loginFeature1}
              onChange={(e) => handleChange('loginFeature1', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="🔬 Digital Pathology Management"
            />
            <p className="text-xs text-gray-500 mt-1">Include emoji at start for best display</p>
          </div>

          {/* Feature 2 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Feature 2
            </label>
            <input
              type="text"
              value={settings.loginFeature2}
              onChange={(e) => handleChange('loginFeature2', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="📊 Advanced Analytics & Reports"
            />
          </div>

          {/* Feature 3 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Feature 3
            </label>
            <input
              type="text"
              value={settings.loginFeature3}
              onChange={(e) => handleChange('loginFeature3', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="🔧 Device Management & Monitoring"
            />
          </div>

          {/* Feature 4 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Feature 4
            </label>
            <input
              type="text"
              value={settings.loginFeature4}
              onChange={(e) => handleChange('loginFeature4', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="🔒 Secure & HIPAA Compliant"
            />
          </div>
        </div>
      </div>

      {/* Logo Upload Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Logo & Icon</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Logo Upload */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Logo Upload
            </label>
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300">
              {!logoPreview && !settings.logoUrl ? (
                <div className="text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Upload Custom Logo</h4>
                  <p className="text-xs text-gray-500 mb-3">
                    SVG, PNG, or JPG (max 5MB)
                  </p>
                  <label className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 cursor-pointer">
                    <Upload className="w-4 h-4 mr-2" />
                    Choose File
                    <input
                      type="file"
                      accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </label>
                  {uploadError && (
                    <p className="text-xs text-red-600 mt-2">{uploadError}</p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-20 h-20 bg-white rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden">
                        <img
                          src={logoPreview || settings.logoUrl}
                          alt="Logo preview"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Logo Uploaded</p>
                        <p className="text-xs text-gray-500">This will replace the emoji icon</p>
                      </div>
                    </div>
                    <button
                      onClick={handleRemoveLogo}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove logo"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <label className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 cursor-pointer">
                    <Upload className="w-3.5 h-3.5 mr-2" />
                    Change Logo
                    <input
                      type="file"
                      accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Upload a custom logo to replace the emoji icon. The logo will be displayed in the header and login page.
            </p>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">📝 Important Notes</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>✅ Upload a custom logo (SVG, PNG, JPG) to replace the emoji icon</li>
          <li>✅ Changes to System Name, Icon, and Logo apply immediately after saving</li>
          <li>✅ Header and Login page update automatically (no refresh needed)</li>
          <li>✅ Page title (browser tab) updates immediately</li>
          <li>✅ Logo files are stored as base64 in browser localStorage (max 5MB)</li>
          <li>⚠️ Color changes require code rebuild for full application (coming soon)</li>
        </ul>
      </div>

      {/* Save Button */}
      <div className="flex justify-end space-x-3">
        <button
          onClick={handleSaveSettings}
          disabled={!hasChanges || saving}
          className={`px-6 py-2 rounded-lg font-medium flex items-center ${
            hasChanges && !saving
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Branding Settings'}
        </button>
      </div>
    </div>
  );
};

export default BrandingSettings;
